import { ModalViewModel } from '@/infra/ModalViewModel'
import { action, computed, observable } from 'mobx'
import { required, validationContext } from 'validx'
import {
  type AllowedProgressionTypes,
  type AvailableProgressionOption,
  type AvailableProgressionStep,
  AvailableProgressionSteps,
} from '@/features/inventory/allowed-progressions'
import type { SelectedProgression, StepMap } from './ProgressionEditorViewModel'
import {
  type GetBillingType,
  RegularEditorViewModel,
} from './RegularEditorViewModel'

export class JobProgressionBillingModalViewModel {
  readonly modal = new ModalViewModel()

  /**
   * The given progression for this modal
   */
  @observable
  accessor selectedProgression: AvailableProgressionOption | undefined =
    undefined

  /**
   * The selected steps for this progression
   */
  @observable
  accessor selectedSteps: StepMap = new Map()

  /**
   * Validation to be used on this modal
   */
  readonly validation = validationContext(this, {
    selectedProgression: [required('This field is required.')],
    selectedSteps: [
      ({ value }: { value: StepMap }) =>
        value.size > 0 || 'Select at least one step',
    ],
  })

  constructor(
    private readonly _getAvailableProgressions: () => AvailableProgressionOption[],
    private readonly _setProgression: (
      progression: AllowedProgressionTypes,
      selected: SelectedProgression,
    ) => void,
    private readonly _getBillingType: GetBillingType,
  ) {}

  /**
   * Non selected available progressions
   */
  @computed
  get availableProgressions() {
    return this._getAvailableProgressions()
  }

  /**
   * Available steps for this progression
   */
  @computed
  get availableSteps(): AvailableProgressionStep[] {
    return this.selectedProgression
      ? AvailableProgressionSteps[this.selectedProgression.value]
      : []
  }

  /**
   * Steps selected to be used with this progression
   */
  @computed
  get selectedStepsList() {
    return Array.from(this.selectedSteps.values())
  }

  /**
   * Triggers the modal
   */
  @action.bound
  show(selectedProgression?: SelectedProgression) {
    if (selectedProgression) {
      const { name, value, steps } = selectedProgression

      this.selectedProgression = { name, value }

      // The modal edits copies, so cancelling keeps the saved prices.
      steps.forEach((step) => {
        const pricingVm = new RegularEditorViewModel(this._getBillingType)
        pricingVm.hydrate(step.pricingVm.toValues())
        this.selectedSteps.set(step.value, { ...step, pricingVm })
      })
    }

    this.modal.show()
  }

  /**
   * Dismisses the modal
   */
  @action.bound
  close() {
    this.reset()
    this.modal.close()
  }

  /**
   * Selects the progression to be used
   */
  @action.bound
  setSelectedProgression(progression?: AvailableProgressionOption | null) {
    this.selectedProgression = progression ?? undefined
    this.selectedSteps.clear()
  }

  /**
   * Assigns the steps for this progression
   */
  @action.bound
  setSelectedSteps(steps: AvailableProgressionStep[]) {
    this.selectedSteps = new Map(
      steps.map((step) => [
        step.value,
        this.selectedSteps.get(step.value) ?? {
          ...step,
          pricingVm: new RegularEditorViewModel(this._getBillingType),
        },
      ]),
    )
  }

  /**
   * Clears the values
   */
  @action.bound
  reset() {
    this.selectedProgression = undefined
    this.selectedSteps.clear()
  }

  /**
   * Validates this modal
   */
  validate() {
    return (
      this.validation.reset().validate().isValid &&
      Array.from(this.selectedSteps.values()).every((step) =>
        step.pricingVm.validate(),
      )
    )
  }

  /**
   * Saves this data
   */
  @action.bound
  save() {
    if (this.validate() && this.selectedProgression) {
      this._setProgression(this.selectedProgression.value, {
        ...this.selectedProgression,
        steps: Array.from(this.selectedSteps.values()),
      })

      this.close()
    }
  }
}
